/**
 * SG-A: strategy-owned block generation mode.
 *
 * The VALUES are owned by the schema package — `StrategyBlockGenerationMode` in
 * `@segmently/schema-models/strategy` (`Strategy.blockGenerationMode`, optional).
 * This module is the single runtime home for the guard, the request normalizer,
 * the readback resolver and the user-facing labels, so the API controller, the
 * CLI, the pipeline orchestrator and the creation form all agree.
 *
 * Contract:
 *   - ABSENT (or explicitly cleared to null) resolves to `parallel`. Every
 *     strategy created before this field keeps byte-for-byte today's behaviour.
 *   - An invalid value NEVER silently degrades: a request is rejected (400) and
 *     a corrupted stored value raises instead of being read as `parallel`.
 *   - Deliberately NOT named `generationMode`: content-plan
 *     (`ContentPost.generationMode`) and ad-creative already use that bare name
 *     with different semantics.
 */
import type { StrategyBlockGenerationMode as SchemaStrategyBlockGenerationMode } from '@segmently/schema-models/strategy';

export const STRATEGY_BLOCK_GENERATION_MODES = ['parallel', 'sequential_by_branch'] as const;

export type StrategyBlockGenerationMode = (typeof STRATEGY_BLOCK_GENERATION_MODES)[number];

/**
 * Compile-time proof that the runtime list above stays identical to the
 * schema-generated union. If the schema adds or renames a value, one of these
 * two assignments stops compiling.
 */
const _schemaToLocal: StrategyBlockGenerationMode = undefined as unknown as SchemaStrategyBlockGenerationMode;
const _localToSchema: SchemaStrategyBlockGenerationMode = undefined as unknown as StrategyBlockGenerationMode;
void _schemaToLocal;
void _localToSchema;

/** Absent field = historic concurrent dispatch. */
export const DEFAULT_STRATEGY_BLOCK_GENERATION_MODE: StrategyBlockGenerationMode = 'parallel';

/**
 * SG-A (T7c vi) — where the FROZEN task mode came from, for readback:
 *   - request:        the per-run API/CLI option (never written to the strategy head)
 *   - strategy:       the strategy head's saved setting
 *   - default:        head absent/legacy (no field) → explicit `parallel`
 *   - inherited:      a retry/resume task copied the SOURCE task's frozen value
 *   - legacy-default: a task dispatched before the freeze existed, stamped
 *                     `parallel` by the executor at run time
 */
export const STRATEGY_BLOCK_GENERATION_MODE_SOURCES = ['request', 'strategy', 'default', 'inherited', 'legacy-default'] as const;
export type StrategyBlockGenerationModeSource = (typeof STRATEGY_BLOCK_GENERATION_MODE_SOURCES)[number];

/**
 * Task-creation freeze: the per-run request option wins; otherwise the
 * strategy head's saved setting (legacy absent → `parallel`, source `default`).
 * A corrupted head THROWS (`resolveBlockGenerationMode`) — creation must fail
 * with a clear 400, never a silent parallel.
 */
export function freezeBlockGenerationMode(
  requestMode: StrategyBlockGenerationMode | undefined,
  strategyHead: { blockGenerationMode?: unknown } | Record<string, unknown> | null | undefined,
): { mode: StrategyBlockGenerationMode; source: StrategyBlockGenerationModeSource } {
  if (requestMode) return { mode: requestMode, source: 'request' };
  const stored = (strategyHead as { blockGenerationMode?: unknown } | null | undefined)?.blockGenerationMode;
  const mode = resolveBlockGenerationMode(strategyHead);
  return { mode, source: stored === undefined || stored === null ? 'default' : 'strategy' };
}

/**
 * Plain-language copy for the creation form (Task 7b). Describes the execution
 * difference only — no speed, cost or quality promises, none of which are
 * measured yet.
 */
export const STRATEGY_BLOCK_GENERATION_MODE_LABELS: Readonly<Record<
  StrategyBlockGenerationMode,
  { label: string; description: string }
>> = {
  parallel: {
    label: 'Parallel',
    description: 'All blocks are generated at the same time.',
  },
  sequential_by_branch: {
    label: 'Sequential within each branch',
    description:
      'A block starts only after the block it follows has been generated and saved; separate branches still run at the same time.',
  },
};

const ACCEPTED_VALUES = STRATEGY_BLOCK_GENERATION_MODES.join(', ');

/** Carries the field name so callers can answer with a 400 body without re-deriving it. */
export class StrategyBlockGenerationModeError extends Error {
  readonly field = 'blockGenerationMode' as const;

  constructor(message: string) {
    super(message);
    this.name = 'StrategyBlockGenerationModeError';
  }
}

export function isStrategyBlockGenerationMode(value: unknown): value is StrategyBlockGenerationMode {
  return typeof value === 'string'
    && (STRATEGY_BLOCK_GENERATION_MODES as readonly string[]).includes(value);
}

/**
 * Request-side normalizer. An omitted option stays `undefined` so the field is
 * never written; anything else must be one of the two accepted values.
 *
 * @throws {StrategyBlockGenerationModeError} on any present-but-invalid value.
 */
export function normalizeBlockGenerationModeRequest(
  value: unknown,
): StrategyBlockGenerationMode | undefined {
  if (value === undefined || value === null) return undefined;
  if (!isStrategyBlockGenerationMode(value)) {
    throw new StrategyBlockGenerationModeError(
      `blockGenerationMode must be one of: ${ACCEPTED_VALUES}`,
    );
  }
  return value;
}

/**
 * Readback/executor resolver: the effective mode for a persisted strategy head.
 * The executor freezes THIS value on the task, so an absent legacy field becomes
 * an explicit `parallel` rather than an implicit one.
 *
 * @throws {StrategyBlockGenerationModeError} when the stored value is present
 *   but unrecognised — a corrupted head must be visible, not read as parallel.
 */
export function resolveBlockGenerationMode(
  strategy: { blockGenerationMode?: unknown } | Record<string, unknown> | null | undefined,
): StrategyBlockGenerationMode {
  const stored = (strategy as { blockGenerationMode?: unknown } | null | undefined)
    ?.blockGenerationMode;
  if (stored === undefined || stored === null) return DEFAULT_STRATEGY_BLOCK_GENERATION_MODE;
  if (!isStrategyBlockGenerationMode(stored)) {
    throw new StrategyBlockGenerationModeError(
      `Stored blockGenerationMode ${JSON.stringify(stored)} is not one of: ${ACCEPTED_VALUES}`,
    );
  }
  return stored;
}
